import { CarConfiMarketConfiguration } from '~/app/market-config/models/car-confi-market-configuration';
import { getModelIdString, getModelIdStringFromPr7Extension } from '~/graphql/mapper/model-id-string';
import {
	CarlineQuery,
	DriveTrainModelsFragment,
	FinancingError,
	IncentiveData,
	ModelIdentifier,
	MonthlyRate,
	Price,
	Status,
	TechnicalDataForModel,
} from '../__generated__/graphql';
import { FeatureCategory } from '../types';

export interface DriveTrain {
	type: 'driveTrain';
	__item: DriveTrainModelsFragment;
	modelIdStringPR3: string;
	modelIdString: string;
	incentives: IncentiveData[];
	featureId: string;
	carline?: string;
	pr7: string;
	debugCode: string;
	name: string;
	modelId: ModelIdentifier;
	status: Status;
	techData: TechnicalDataForModel | undefined | null;
	totalPrice: Price;
	monthlyRate: MonthlyRate | FinancingError;

	hidden: boolean;
}

export function mapCarline(response: CarlineQuery, config: CarConfiMarketConfiguration, debug = false) {
	const trimlinesFromCatalog = response.configuredCar?.catalog?.models?.data?.map((t) => ({
		...t,
		getModelIdString: getModelIdString(t?.identifier),
	}));

	const multipleEngines = response.configuredCar?.carlineStructureCarline?.trimlines?.find(
		(trimline) => trimline?.models?.filter(({ hidden }) => debug || !hidden)?.length > 1,
	);

	const carline = response.configuredCar.carlineStructureCarline?.name ?? 'no carline name provided';

	const getModelStatus = (modelId: ModelIdentifier): Status => {
		return (
			trimlinesFromCatalog?.find(
				(trimline) => trimline.getModelIdString === getModelIdStringFromPr7Extension(modelId),
			)?.status ?? defaultStatus
		);
	};

	const trimlines = response.configuredCar?.carlineStructureCarline?.trimlines
		.map((trimline) => {
			const cheapestModelPrice = () => {
				if (trimline.models) {
					const sortedByPrice = [...trimline.models].sort((a, b) => {
						const aPrice: number = a.prices.priceWithFees?.value || 0;
						const bPrice: number = b.prices.priceWithFees?.value || 0;
						return aPrice - bPrice;
					});
					return sortedByPrice[0].prices.priceWithFees!;
				}
				return undefined;
			};

			const totalPrice = config.showPricesWithFees ? cheapestModelPrice() : trimline.prices.minPrice;

			return {
				type: 'trimline' as const satisfies FeatureCategory,
				__item: trimline,
				featureId: trimline.id,
				name: multipleEngines ? trimline.name : trimline.models[0].engineName, // if we show the combined card we want the cdb engine name
				model: trimlinesFromCatalog?.find((t) => trimline.id === t?.trimlineId)?.identifier,
				status:
					trimlinesFromCatalog?.find((t) => trimline.id === t?.trimlineId && t.status?.selected)?.status ??
					trimlinesFromCatalog?.find((t) => trimline.id === t?.trimlineId)?.status ??
					defaultStatus,
				totalPrice,
				monthlyRate: trimline.prices.monthlyRate,
				pr7: trimline.id,
				driveTrains: trimline.models
					.filter(({ hidden }) => debug || !hidden)
					.map((model) =>
						mapDriveTrains(model, config.showPricesWithFees, carline, getModelStatus(model.modelId)),
					),
			};
		})
		.filter(({ driveTrains }) => driveTrains.length > 0);

	return {
		carInfo: {
			carIdentifier: response.configuredCar.id,
			carline: response.configuredCar.carlineStructureCarline?.name,
			model: response.configuredCar.model?.name,
			incentives: response.configuredCar.incentives,
			technicalData: {
				consumption: 'Not available',
				emission: 'Not available',
			},
			// TODO: Find replacement for the summaries
			// technicalData: {
			// 	consumption:
			// 		response.configuredCar.technicalDataV2?.consumptionAndEmission?.summary?.consumptionSummary,
			// 	emission: response.configuredCar.technicalDataV2?.consumptionAndEmission?.summary?.emissionCO2Summary,
			// },
		},
		trimlines,
	};
}

const mapDriveTrains = (
	model: DriveTrainModelsFragment,
	showPricesWithFees: boolean,
	carline: string,
	status: Status,
): DriveTrain => {
	const modelIdStringPR3 = getModelIdString(model.modelId);

	const modelIdString = getModelIdStringFromPr7Extension(model.modelId);
	const totalPriceDriveTrain = showPricesWithFees ? model.prices.priceWithFees : model.prices.price;

	return {
		type: 'driveTrain' as const satisfies FeatureCategory,
		__item: model,
		modelIdStringPR3,
		modelIdString,
		incentives: (model.incentives || []).filter((incentive): incentive is IncentiveData => incentive != null),
		featureId: model.modelId.code,
		carline,
		pr7: model.modelId.code,
		debugCode: `${model.modelId.code}\\${model.modelId.version}${model.modelId.extensionsPR7?.length ? `\\${model.modelId.extensionsPR7.join('\\')}` : ''}\\${model.modelId.year}`,
		name: model.engineName,
		modelId: model.modelId,
		status,
		techData: model.technicalDataV2,
		totalPrice: totalPriceDriveTrain ?? { __typename: 'Price', value: -1 },
		monthlyRate: model.prices.monthlyRate ?? {
			__typename: 'FinancingError',
			code: 'no monthly rate available',
			message: 'no monthly rate available',
			type: '',
		},
		hidden: !!model.hidden,
	};
};

export type CarlineInfo = ReturnType<typeof mapCarline>;

/** Used as a fallback value to satisfy types. */
const defaultStatus: Status = {
	__typename: 'Status',
	available: true,
	inConflict: false,
	selected: false,
	changeable: true,
	standard: false,
};
